import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  COUNTRIES,
  findCountryByCode,
  COUNTRY_NOT_LISTED,
} from "../../utils/countries";
import BaseIcon from "../BaseIcon";
import "flag-icons/css/flag-icons.min.css";
import "./style.scss";

export default function CountrySelector({
  value,
  onChange,
  onOpenChange,
  className = "",
  buttonRef,
}) {
  CountrySelector.propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    onOpenChange: PropTypes.func,
    className: PropTypes.string,
    buttonRef: PropTypes.object,
  };
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapperRef = useRef(null);
  const searchRef = useRef(null);
  const country = findCountryByCode(value);

  // item 1: список по АЛФАВИТУ и с поиском. Сортируем по ПЕРЕВЕДЁННОМУ названию
  // (в ru и en порядок разный), а не по коду — раньше страны шли группами по локали
  // магазина (RU, затем EN-магазин, затем DE-магазин) и выглядели «в разнобой».
  const visibleCountries = useMemo(() => {
    const q = query.trim().toLowerCase();
    return COUNTRIES.map((c) => ({ ...c, label: t(c.nameKey) }))
      .filter((c) => !q || c.label.toLowerCase().includes(q))
      .sort((a, b) => a.label.localeCompare(b.label, i18n.language || "ru"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, i18n.language, t]);

  // фокус в поиск при открытии; на моб. не форсируем клавиатуру автоматически
  useEffect(() => {
    if (!open) { setQuery(""); return; }
    const isTouch = typeof window !== "undefined" && window.matchMedia
      && window.matchMedia("(pointer: coarse)").matches;
    if (!isTouch) searchRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        onOpenChange && onOpenChange(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("touchstart", onDocClick);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("touchstart", onDocClick);
    };
  }, [open, onOpenChange]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    onOpenChange && onOpenChange(next);
  };

  const handleSelect = (code) => {
    onChange(code);
    setOpen(false);
    onOpenChange && onOpenChange(false);
  };

  return (
    <div ref={wrapperRef} className={`country-selector ${className}`}>
      <button
        ref={buttonRef}
        type="button"
        className="country-selector__button"
        onClick={toggle}
      >
        {country ? (
          <>
            <span className={`fi fi-${country.flag} country-selector__flag`} />
            <span className="country-selector__name">{t(country.nameKey)}</span>
          </>
        ) : value === COUNTRY_NOT_LISTED ? (
          <span className="country-selector__name">
            {t("shop_modal.not_listed")}
          </span>
        ) : (
          <span className="country-selector__placeholder">
            {t("shop_modal.choose_country")}
          </span>
        )}
        <span className="country-selector__chevron">
          <BaseIcon name="lang_chevrons" />
        </span>
      </button>
      {open && (
        <div className="country-selector__dropdown" data-allow-touch-scroll>
          {/* item 1: вторая строка «Выбери страну» была статичным дублем заголовка и
              ничего не делала. Теперь это поле поиска — можно набрать свою страну. */}
          <input
            ref={searchRef}
            type="text"
            className="country-selector__search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("shop_modal.search_country")}
            aria-label={t("shop_modal.search_country")}
          />
          {visibleCountries.map((c) => (
            <button
              key={c.code}
              type="button"
              className="country-selector__option"
              onClick={() => handleSelect(c.code)}
            >
              <span className={`fi fi-${c.flag} country-selector__flag`} />
              <span className="country-selector__name">{c.label}</span>
            </button>
          ))}
          {!visibleCountries.length && (
            <div className="country-selector__empty">
              {t("shop_modal.nothing_found")}
            </div>
          )}
          {/* item 1: последним пунктом — «Нет в списке». Выбор фиксирует регион без
              магазина: показывается попап «магазин в разработке», а иконка магазина
              перестаёт пульсировать (иначе выключить пульсацию было нечем). */}
          <button
            key={COUNTRY_NOT_LISTED}
            type="button"
            className="country-selector__option country-selector__option--not-listed"
            onClick={() => handleSelect(COUNTRY_NOT_LISTED)}
          >
            <span className="country-selector__name">
              {t("shop_modal.not_listed")}
            </span>
          </button>
        </div>
      )}
    </div>
  );
}
